import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Pencil, Trash2, RefreshCw, Loader2, Package, FileText, Phone, Mail, Building2, Inbox, Sparkles } from 'lucide-react';
import axios from 'axios';
import adminAxios from './adminApi';
import ProductEditModal from './ProductEditModal';
import GreetingsTab from './GreetingsTab';
import { resolveImageUrl } from '../../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Tab = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition ${
      active ? 'bg-blue-600 text-white shadow' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
    }`}
  >
    <Icon size={16} />
    {label}
    {typeof count === 'number' && (
      <span className={`text-xs rounded-full px-2 py-0.5 ${active ? 'bg-blue-700' : 'bg-gray-100 text-gray-600'}`}>{count}</span>
    )}
  </button>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product object or {} for new

  const email = sessionStorage.getItem('hvac_admin_email') || '';

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, q] = await Promise.all([
        axios.get(`${API}/products`).then((r) => r.data),
        adminAxios.get('/admin/quotes').then((r) => r.data),
      ]);
      setProducts(p);
      setQuotes(q);
    } catch (err) {
      console.error('Failed to load admin data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const logout = () => {
    sessionStorage.removeItem('hvac_admin_token');
    sessionStorage.removeItem('hvac_admin_email');
    navigate('/admin/login', { replace: true });
  };

  const handleProductSaved = async () => {
    setEditing(null);
    await loadAll();
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Delete "${product.full_name}"? This can't be undone.`)) return;
    try {
      await adminAxios.delete(`/admin/products/${product.id}`);
      await loadAll();
    } catch (err) {
      alert('Failed to delete product');
    }
  };

  const handleToggleStock = async (product) => {
    try {
      await adminAxios.put(`/admin/products/${product.id}`, { in_stock: !product.in_stock });
      await loadAll();
    } catch (err) {
      alert('Failed to toggle stock');
    }
  };

  const handleDeleteQuote = async (quote) => {
    if (!window.confirm(`Delete the quote from ${quote.name}?`)) return;
    try {
      await adminAxios.delete(`/admin/quotes/${quote.id}`);
      await loadAll();
    } catch (err) {
      alert('Failed to delete quote');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Owner Dashboard</h1>
            <p className="text-xs text-gray-500">{email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50" data-testid="admin-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-black" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 mb-6 flex-wrap">
          <Tab active={tab === 'products'} onClick={() => setTab('products')} icon={Package} label="Products" count={products.length} testId="admin-tab-products" />
          <Tab active={tab === 'quotes'} onClick={() => setTab('quotes')} icon={FileText} label="Quote Requests" count={quotes.length} testId="admin-tab-quotes" />
          <Tab active={tab === 'greetings'} onClick={() => setTab('greetings')} icon={Sparkles} label="Greetings" testId="admin-tab-greetings" />
        </div>

        {loading && (
          <div className="bg-white rounded-xl p-12 text-center text-gray-500 border border-gray-200">
            <Loader2 className="animate-spin mx-auto mb-2 text-blue-600" size={28} />
            Loading...
          </div>
        )}

        {!loading && tab === 'products' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Manage Products</h2>
              <button onClick={() => setEditing({})} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold text-sm" data-testid="admin-add-product">
                <Plus size={16} /> Add product
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-3">Product</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Tiers</th>
                      <th className="text-left px-4 py-3">Stock</th>
                      <th className="text-right px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm">
                    {products.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50" data-testid={`admin-product-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {p.image_url ? (
                              <img src={resolveImageUrl(p.image_url)} alt={p.full_name} className="w-12 h-12 rounded object-cover border" />
                            ) : (
                              <div className="w-12 h-12 rounded bg-gray-100 border" />
                            )}
                            <div>
                              <div className="font-semibold text-gray-900">{p.full_name}</div>
                              <div className="text-xs text-gray-500">{p.name} &middot; {p.weight}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-600">
                          {p.price_tiers.map((t) => `${t.quantity}pc: $${t.price.toFixed(2)}`).join(' · ')}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => handleToggleStock(p)} className={`text-xs font-semibold px-2 py-1 rounded ${p.in_stock ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`} data-testid={`admin-toggle-stock-${p.id}`}>
                            {p.in_stock ? 'IN STOCK' : 'OUT OF STOCK'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => setEditing(p)} className="text-blue-600 hover:bg-blue-50 p-2 rounded" title="Edit" data-testid={`admin-edit-product-${p.id}`}>
                              <Pencil size={16} />
                            </button>
                            <button onClick={() => handleDeleteProduct(p)} className="text-red-500 hover:bg-red-50 p-2 rounded" title="Delete" data-testid={`admin-delete-product-${p.id}`}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {products.length === 0 && (
                      <tr><td colSpan={4} className="text-center text-gray-500 py-8">No products yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {!loading && tab === 'quotes' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Quote Requests</h2>
              <p className="text-sm text-gray-500">{quotes.length} total</p>
            </div>

            {quotes.length === 0 && (
              <div className="bg-white rounded-xl p-12 text-center text-gray-500 border border-gray-200">
                <Inbox className="mx-auto mb-3 text-gray-400" size={36} />
                No quote requests yet. New requests will appear here automatically.
              </div>
            )}

            <div className="space-y-3">
              {quotes.map((q) => (
                <details key={q.id} className="bg-white rounded-xl border border-gray-200 group" data-testid={`admin-quote-row-${q.id}`}>
                  <summary className="cursor-pointer p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900">{q.name} <span className="text-gray-400 font-normal">&middot; {q.company || 'No company'}</span></div>
                      <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap mt-1">
                        <span className="flex items-center gap-1"><Mail size={12} />{q.email}</span>
                        <span className="flex items-center gap-1"><Phone size={12} />{q.phone}</span>
                        <span>{new Date(q.timestamp).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">${q.total.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{q.cart_items.length} item(s)</div>
                    </div>
                  </summary>
                  <div className="border-t px-4 py-3 bg-gray-50">
                    <table className="w-full text-sm">
                      <thead className="text-xs uppercase text-gray-500">
                        <tr><th className="text-left py-1">Product</th><th className="text-center py-1">Qty</th><th className="text-right py-1">Unit</th><th className="text-right py-1">Subtotal</th></tr>
                      </thead>
                      <tbody>
                        {q.cart_items.map((i, idx) => (
                          <tr key={idx} className="border-t border-gray-200">
                            <td className="py-1">{i.product_name}</td>
                            <td className="text-center py-1">{i.quantity}</td>
                            <td className="text-right py-1">${i.unit_price.toFixed(2)}</td>
                            <td className="text-right py-1">${i.total.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {q.message && (
                      <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-400 text-sm">
                        <Building2 size={12} className="inline mr-1" /><strong>Message:</strong> {q.message}
                      </div>
                    )}
                    <div className="mt-3 flex justify-end gap-2">
                      <a href={`mailto:${q.email}`} className="text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded">Reply via email</a>
                      <a href={`tel:${q.phone}`} className="text-sm font-semibold bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded">Call</a>
                      <button onClick={() => handleDeleteQuote(q)} className="text-sm font-semibold bg-gray-100 hover:bg-red-100 hover:text-red-700 text-gray-700 px-3 py-1.5 rounded" data-testid={`admin-delete-quote-${q.id}`}>Delete</button>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {!loading && tab === 'greetings' && (
          <GreetingsTab />
        )}
      </div>

      {editing !== null && (
        <ProductEditModal
          product={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={handleProductSaved}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
